import React, { useState } from 'react';
import { ShoppingCart, Plus } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { addToCart, supplements } from '../data/mock';
import { toast } from 'sonner';

const SUPPLEMENT_CATEGORIES = [
  { key: 'viande', label: 'Viandes / Protéines (+2€)' },
  { key: 'fromage', label: 'Fromages (+1€)' },
  { key: 'legume', label: 'Légumes (+1€)' },
  { key: 'autre', label: 'Autres (+1€)' },
  { key: 'special', label: 'Spéciaux' },
];

const FREE_INGREDIENTS_COUNT = 3;

const PizzaCard = ({ pizza }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [showCustomization, setShowCustomization] = useState(false);
  const [selectedSupplements, setSelectedSupplements] = useState([]);
  const [isChausson, setIsChausson] = useState(false);

  // Composer-specific state
  const [showComposer, setShowComposer] = useState(false);
  const [composerBase, setComposerBase] = useState('tomate');
  const [composerCheese, setComposerCheese] = useState('emmental');
  const [composerIngredients, setComposerIngredients] = useState([]);

  const isPizza = pizza.category && !['boisson', 'salade', 'dessert'].includes(pizza.category);
  const isComposer = pizza.name === "Pizza à Composer";

  const handleQuickAdd = () => {
    if (isComposer) {
      setShowComposer(true);
      return;
    }
    setIsAdding(true);
    const item = isChausson
      ? { ...pizza, id: `${pizza.id}-ch`, name: `${pizza.name} (Chausson)`, chausson: true }
      : pizza;
    addToCart(item);
    toast.success(`${item.name} ajoutée au panier !`, { duration: 2000 });
    window.dispatchEvent(new Event('storage'));
    setTimeout(() => setIsAdding(false), 500);
  };

  const handleCustomize = () => {
    if (isComposer) {
      setShowComposer(true);
      return;
    }
    setShowCustomization(true);
  };

  const toggleSupplement = (supplement) => {
    setSelectedSupplements(prev => {
      const exists = prev.find(s => s.id === supplement.id);
      if (exists) {
        return prev.filter(s => s.id !== supplement.id);
      } else {
        return [...prev, supplement];
      }
    });
  };

  const calculateTotal = () => {
    const supplementsTotal = selectedSupplements.reduce((sum, sup) => sum + sup.price, 0);
    return pizza.price + supplementsTotal;
  };

  const handleAddWithSupplements = () => {
    const baseName = selectedSupplements.length > 0
      ? `${pizza.name} + ${selectedSupplements.map(s => s.name).join(', ')}`
      : pizza.name;
    const displayName = isChausson ? `${baseName} (Chausson)` : baseName;

    const customPizza = {
      ...pizza,
      id: isChausson ? `${pizza.id}-ch` : pizza.id,
      supplements: selectedSupplements,
      price: calculateTotal(),
      customName: displayName,
      chausson: isChausson
    };

    addToCart(customPizza);
    toast.success(`${displayName} ajoutée au panier !`, { duration: 2000 });
    window.dispatchEvent(new Event('storage'));
    setShowCustomization(false);
    setSelectedSupplements([]);
  };

  // --- Composer Logic ---
  const toggleComposerIngredient = (ingredient) => {
    setComposerIngredients(prev => {
      const exists = prev.find(s => s.id === ingredient.id);
      if (exists) {
        return prev.filter(s => s.id !== ingredient.id);
      } else {
        return [...prev, ingredient];
      }
    });
  };

  const getComposerPaidIngredients = () => {
    if (composerIngredients.length <= FREE_INGREDIENTS_COUNT) return [];
    return composerIngredients.slice(FREE_INGREDIENTS_COUNT);
  };

  const calculateComposerTotal = () => {
    const paidIngredients = getComposerPaidIngredients();
    const extraCost = paidIngredients.reduce((sum, ing) => sum + ing.price, 0);
    return pizza.price + extraCost;
  };

  const handleAddComposer = () => {
    const baseLabel = composerBase === 'tomate' ? 'Base Tomate' : 'Base Crème';
    const cheeseLabel = composerCheese === 'emmental' ? 'Emmental' : 'Mozzarella';
    const ingredientNames = composerIngredients.map(i => i.name).join(', ');

    let fullName = `Pizza Composée (${baseLabel}, ${cheeseLabel})`;
    if (ingredientNames) fullName += ` + ${ingredientNames}`;
    if (isChausson) fullName += ' (Chausson)';

    const composerId = `composer-${composerBase}-${composerCheese}-${composerIngredients.map(i => i.id).join('-')}${isChausson ? '-ch' : ''}`;

    const composerPizza = {
      ...pizza,
      id: composerId,
      name: fullName,
      customName: fullName,
      price: calculateComposerTotal(),
      chausson: isChausson,
      composerDetails: {
        base: composerBase,
        cheese: composerCheese,
        freeIngredients: composerIngredients.slice(0, FREE_INGREDIENTS_COUNT).map(i => i.name),
        paidIngredients: getComposerPaidIngredients().map(i => i.name)
      }
    };

    addToCart(composerPizza);
    toast.success(`${fullName} ajoutée au panier !`, { duration: 2000 });
    window.dispatchEvent(new Event('storage'));
    setShowComposer(false);
    setComposerIngredients([]);
    setComposerBase('tomate');
    setComposerCheese('emmental');
  };

  // All non-cheese supplements for the composer
  const composerSupplements = supplements.filter(s => s.type !== 'fromage');

  return (
    <>
      <div
        className="group relative bg-gradient-to-br from-gray-900 to-black rounded-xl overflow-hidden border border-gray-800 hover:border-red-600/50 transition-all duration-300 transform hover:scale-[1.02] hover:shadow-2xl hover:shadow-red-900/30"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        data-testid={`pizza-card-${pizza.id}`}
      >
        {/* Image Container */}
        <div className="relative h-56 overflow-hidden">
          <img
            src={pizza.image}
            alt={pizza.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-60 group-hover:opacity-80 transition-opacity"></div>

          {pizza.specialty && (
            <Badge className="absolute top-3 left-3 bg-red-600 text-white border-none font-semibold px-3 py-1">
              Spécialité
            </Badge>
          )}

          <div className="absolute bottom-3 right-3 bg-red-600 text-white font-bold px-4 py-2 rounded-lg shadow-lg">
            {pizza.price.toFixed(2)}€
          </div>
        </div>

        {/* Content */}
        <div className="p-5">
          <h3 className="text-xl font-bold text-white mb-2 group-hover:text-red-400 transition-colors">
            {pizza.name}
          </h3>
          <p className="text-gray-400 text-sm mb-3 line-clamp-2 leading-relaxed">
            {pizza.ingredients}
          </p>

          {/* Chausson Toggle */}
          {isPizza && (
            <div
              className="flex items-center gap-2 mb-3 cursor-pointer"
              onClick={() => setIsChausson(!isChausson)}
              data-testid={`chausson-toggle-${pizza.id}`}
            >
              <Checkbox
                checked={isChausson}
                onCheckedChange={setIsChausson}
                id={`chausson-${pizza.id}`}
                className="border-gray-600 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
              />
              <label htmlFor={`chausson-${pizza.id}`} className="text-sm text-gray-300 cursor-pointer select-none">
                En chausson (gratuit)
              </label>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-800">
            <div className="text-2xl font-bold text-white">
              {pizza.price.toFixed(2)}€
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleCustomize}
                variant="outline"
                className="border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white"
              >
                {isComposer ? 'Composer' : 'Supplément'}
              </Button>
              <Button
                size="sm"
                onClick={handleQuickAdd}
                disabled={isAdding}
                className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold shadow-lg shadow-red-600/30"
                data-testid={`add-to-cart-${pizza.id}`}
              >
                <ShoppingCart size={16} className="mr-2" />
                {isAdding ? 'Ajouté!' : isComposer ? 'Composer' : 'Ajouter'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Standard Supplements Dialog */}
      <Dialog open={showCustomization} onOpenChange={setShowCustomization}>
        <DialogContent className="bg-gray-900 text-white border-gray-800 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white">
              Suppléments - {pizza.name}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Ajoutez des suppléments à votre pizza
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4">
            <div className="bg-gradient-to-br from-red-900/20 to-black border border-red-600/30 rounded-lg p-4 mb-6">
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-semibold text-lg text-white">{pizza.name}</h4>
                <span className="text-red-400 font-bold">{pizza.price.toFixed(2)}€</span>
              </div>
              <p className="text-sm text-gray-400">{pizza.ingredients}</p>
            </div>

            {isPizza && (
              <div
                className={`flex items-center gap-3 p-4 rounded-lg border mb-6 cursor-pointer transition-all ${
                  isChausson ? 'bg-red-600/20 border-red-600' : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
                }`}
                onClick={() => setIsChausson(!isChausson)}
                data-testid="chausson-dialog-toggle"
              >
                <Checkbox
                  checked={isChausson}
                  onCheckedChange={setIsChausson}
                  className="border-gray-600 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
                />
                <div>
                  <span className="font-semibold text-white">En Chausson</span>
                  <span className="text-green-400 text-sm ml-2">(Gratuit)</span>
                </div>
              </div>
            )}

            <h4 className="font-semibold text-lg text-white mb-4">Ajouter des suppléments :</h4>
            {SUPPLEMENT_CATEGORIES.map(cat => {
              const items = supplements.filter(s => s.type === cat.key);
              if (items.length === 0) return null;
              return (
                <div key={cat.key} className="mb-4">
                  <p className="text-sm font-semibold text-red-400 mb-2">{cat.label}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {items.map((supplement) => (
                      <div
                        key={supplement.id}
                        onClick={() => toggleSupplement(supplement)}
                        className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                          selectedSupplements.find(s => s.id === supplement.id)
                            ? 'bg-red-600/20 border-red-600 shadow-lg shadow-red-600/20'
                            : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox checked={!!selectedSupplements.find(s => s.id === supplement.id)} className="border-gray-600" />
                          <span className="text-sm font-medium text-white">{supplement.name}</span>
                        </div>
                        <span className="text-sm text-red-400 font-semibold">+{supplement.price.toFixed(2)}€</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            <div className="border-t border-gray-800 pt-4">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <p className="text-gray-400 text-sm">Prix de base</p>
                  <p className="text-white font-semibold">{pizza.price.toFixed(2)}€</p>
                </div>
                {selectedSupplements.length > 0 && (
                  <div>
                    <p className="text-gray-400 text-sm">Suppléments ({selectedSupplements.length})</p>
                    <p className="text-red-400 font-semibold">
                      +{selectedSupplements.reduce((sum, s) => sum + s.price, 0).toFixed(2)}€
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-gray-400 text-sm">Total</p>
                  <p className="text-2xl text-white font-bold">{calculateTotal().toFixed(2)}€</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => { setShowCustomization(false); setSelectedSupplements([]); }}
                  className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-800">
                  Annuler
                </Button>
                <Button onClick={handleAddWithSupplements}
                  className="flex-1 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold"
                  data-testid="add-custom-pizza-btn">
                  <ShoppingCart size={18} className="mr-2" />
                  Ajouter - {calculateTotal().toFixed(2)}€
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Composer Dialog - Special flow for Pizza à Composer */}
      <Dialog open={showComposer} onOpenChange={setShowComposer}>
        <DialogContent className="bg-gray-900 text-white border-gray-800 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white">
              Composez votre Pizza
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Base + Fromage + 3 ingrédients au choix = 12.00€
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-6">
            {/* Step 1: Base */}
            <div>
              <h4 className="font-semibold text-lg text-white mb-3">1. Choisissez votre base</h4>
              <div className="grid grid-cols-2 gap-3">
                {[{ value: 'tomate', label: 'Base Tomate' }, { value: 'creme', label: 'Base Crème' }].map(base => (
                  <button
                    key={base.value}
                    type="button"
                    onClick={() => setComposerBase(base.value)}
                    data-testid={`composer-base-${base.value}`}
                    className={`p-4 rounded-xl border-2 text-center font-semibold transition-all ${
                      composerBase === base.value
                        ? 'border-red-600 bg-red-600/20 text-white'
                        : 'border-gray-700 bg-gray-800/50 text-gray-300 hover:border-gray-600'
                    }`}
                  >
                    {base.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Step 2: Cheese */}
            <div>
              <h4 className="font-semibold text-lg text-white mb-3">2. Choisissez votre fromage</h4>
              <div className="grid grid-cols-2 gap-3">
                {[{ value: 'emmental', label: 'Emmental' }, { value: 'mozzarella', label: 'Mozzarella' }].map(cheese => (
                  <button
                    key={cheese.value}
                    type="button"
                    onClick={() => setComposerCheese(cheese.value)}
                    data-testid={`composer-cheese-${cheese.value}`}
                    className={`p-4 rounded-xl border-2 text-center font-semibold transition-all ${
                      composerCheese === cheese.value
                        ? 'border-red-600 bg-red-600/20 text-white'
                        : 'border-gray-700 bg-gray-800/50 text-gray-300 hover:border-gray-600'
                    }`}
                  >
                    {cheese.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Step 3: Ingredients */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-lg text-white">3. Choisissez vos ingrédients</h4>
                <div className="text-sm">
                  {composerIngredients.length <= FREE_INGREDIENTS_COUNT ? (
                    <span className="text-green-400 font-semibold">
                      {composerIngredients.length}/{FREE_INGREDIENTS_COUNT} gratuits
                    </span>
                  ) : (
                    <span>
                      <span className="text-green-400 font-semibold">{FREE_INGREDIENTS_COUNT} gratuits</span>
                      <span className="text-red-400 font-semibold ml-2">
                        + {composerIngredients.length - FREE_INGREDIENTS_COUNT} payant{composerIngredients.length - FREE_INGREDIENTS_COUNT > 1 ? 's' : ''}
                      </span>
                    </span>
                  )}
                </div>
              </div>

              {composerIngredients.length < FREE_INGREDIENTS_COUNT && (
                <div className="bg-green-600/20 border border-green-500/50 rounded-lg p-3 mb-4">
                  <p className="text-green-400 text-sm font-semibold text-center">
                    Encore {FREE_INGREDIENTS_COUNT - composerIngredients.length} ingrédient{FREE_INGREDIENTS_COUNT - composerIngredients.length > 1 ? 's' : ''} gratuit{FREE_INGREDIENTS_COUNT - composerIngredients.length > 1 ? 's' : ''}
                  </p>
                </div>
              )}

              {composerIngredients.length >= FREE_INGREDIENTS_COUNT && (
                <div className="bg-yellow-600/20 border border-yellow-500/50 rounded-lg p-3 mb-4">
                  <p className="text-yellow-400 text-sm font-semibold text-center">
                    Vos 3 ingrédients gratuits sont choisis. Tout supplément sera facturé.
                  </p>
                </div>
              )}

              {/* Viandes */}
              <div className="mb-3">
                <p className="text-xs font-semibold text-red-400 mb-2">Viandes / Protéines</p>
                <div className="grid grid-cols-2 gap-2">
                  {composerSupplements.filter(s => s.type === 'viande').map((ingredient) => {
                    const isSelected = !!composerIngredients.find(i => i.id === ingredient.id);
                    const selectedIndex = composerIngredients.findIndex(i => i.id === ingredient.id);
                    const isFree = selectedIndex >= 0 && selectedIndex < FREE_INGREDIENTS_COUNT;

                    return (
                      <div
                        key={ingredient.id}
                        onClick={() => toggleComposerIngredient(ingredient)}
                        className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                          isSelected
                            ? isFree ? 'bg-green-600/20 border-green-500' : 'bg-red-600/20 border-red-600'
                            : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Checkbox checked={isSelected} className="border-gray-600" />
                          <span className="text-sm font-medium text-white">{ingredient.name}</span>
                        </div>
                        {isSelected ? (
                          <span className={`text-xs font-bold ${isFree ? 'text-green-400' : 'text-red-400'}`}>
                            {isFree ? 'GRATUIT' : `+${ingredient.price.toFixed(2)}€`}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-500">
                            {composerIngredients.length < FREE_INGREDIENTS_COUNT ? 'Gratuit' : `+${ingredient.price.toFixed(2)}€`}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Légumes */}
              <div className="mb-3">
                <p className="text-xs font-semibold text-red-400 mb-2">Légumes et Autres</p>
                <div className="grid grid-cols-2 gap-2">
                  {composerSupplements.filter(s => s.type === 'legume' || s.type === 'autre').map((ingredient) => {
                    const isSelected = !!composerIngredients.find(i => i.id === ingredient.id);
                    const selectedIndex = composerIngredients.findIndex(i => i.id === ingredient.id);
                    const isFree = selectedIndex >= 0 && selectedIndex < FREE_INGREDIENTS_COUNT;

                    return (
                      <div
                        key={ingredient.id}
                        onClick={() => toggleComposerIngredient(ingredient)}
                        className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                          isSelected
                            ? isFree ? 'bg-green-600/20 border-green-500' : 'bg-red-600/20 border-red-600'
                            : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Checkbox checked={isSelected} className="border-gray-600" />
                          <span className="text-sm font-medium text-white">{ingredient.name}</span>
                        </div>
                        {isSelected ? (
                          <span className={`text-xs font-bold ${isFree ? 'text-green-400' : 'text-red-400'}`}>
                            {isFree ? 'GRATUIT' : `+${ingredient.price.toFixed(2)}€`}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-500">
                            {composerIngredients.length < FREE_INGREDIENTS_COUNT ? 'Gratuit' : `+${ingredient.price.toFixed(2)}€`}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Spéciaux */}
              <div className="mb-3">
                <p className="text-xs font-semibold text-red-400 mb-2">Spéciaux</p>
                <div className="grid grid-cols-2 gap-2">
                  {composerSupplements.filter(s => s.type === 'special').map((ingredient) => {
                    const isSelected = !!composerIngredients.find(i => i.id === ingredient.id);
                    const selectedIndex = composerIngredients.findIndex(i => i.id === ingredient.id);
                    const isFree = selectedIndex >= 0 && selectedIndex < FREE_INGREDIENTS_COUNT;

                    return (
                      <div
                        key={ingredient.id}
                        onClick={() => toggleComposerIngredient(ingredient)}
                        className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                          isSelected
                            ? isFree ? 'bg-green-600/20 border-green-500' : 'bg-red-600/20 border-red-600'
                            : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Checkbox checked={isSelected} className="border-gray-600" />
                          <span className="text-sm font-medium text-white">{ingredient.name}</span>
                        </div>
                        {isSelected ? (
                          <span className={`text-xs font-bold ${isFree ? 'text-green-400' : 'text-red-400'}`}>
                            {isFree ? 'GRATUIT' : `+${ingredient.price.toFixed(2)}€`}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-500">
                            {composerIngredients.length < FREE_INGREDIENTS_COUNT ? 'Gratuit' : `+${ingredient.price.toFixed(2)}€`}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Chausson option */}
            <div
              className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                isChausson ? 'bg-red-600/20 border-red-600' : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
              }`}
              onClick={() => setIsChausson(!isChausson)}
            >
              <Checkbox
                checked={isChausson}
                onCheckedChange={setIsChausson}
                className="border-gray-600 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
              />
              <div>
                <span className="font-semibold text-white">En Chausson</span>
                <span className="text-green-400 text-sm ml-2">(Gratuit)</span>
              </div>
            </div>

            {/* Total & Actions */}
            <div className="border-t border-gray-800 pt-4">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <p className="text-gray-400 text-sm">Pizza Composée</p>
                  <p className="text-white font-semibold">12.00€</p>
                </div>
                {getComposerPaidIngredients().length > 0 && (
                  <div>
                    <p className="text-gray-400 text-sm">Suppléments payants</p>
                    <p className="text-red-400 font-semibold">
                      +{getComposerPaidIngredients().reduce((sum, i) => sum + i.price, 0).toFixed(2)}€
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-gray-400 text-sm">Total</p>
                  <p className="text-2xl text-white font-bold">{calculateComposerTotal().toFixed(2)}€</p>
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => { setShowComposer(false); setComposerIngredients([]); }}
                  className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-800">
                  Annuler
                </Button>
                <Button
                  onClick={handleAddComposer}
                  disabled={composerIngredients.length === 0}
                  className="flex-1 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold disabled:opacity-50"
                  data-testid="add-composer-btn"
                >
                  <ShoppingCart size={18} className="mr-2" />
                  Ajouter - {calculateComposerTotal().toFixed(2)}€
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PizzaCard;
